'use strict'

// A cards list can wrap on its own, or keep a fixed number of cards on
// each row. cardsPerRow is that maximum. Each card then takes an equal
// share of the column. 1 lists them one after another, full width.
// oneCardPerRow is the older flag for that full-width list. Maps and
// calendars share the cards editor and do not store either field.

const { columnFormatRecord } = require('./column-format')

const CARD_VIEW_TYPES = ['cards', 'card']
const CARD_ROW = 'cardRow'
const CARD_COLUMN = 'cardColumn'
const MAX_CARDS_PER_ROW = 12

function isCardView (component) {

  return !!(component && CARD_VIEW_TYPES.includes(component.type))
}

// A card view shows one record: the record that opened the page.
// A cards view lists one card per record.
function isDetailCard (component) {

  return !!(component && component.type === 'card')
}

function isCardLayoutNode (component) {

  return !!(component && (component.type === CARD_ROW || component.type === CARD_COLUMN))
}

// Leaf fields in visual order. A flat list is unchanged. Layout nodes are
// walked and are not themselves fields.
function flattenCardFields (components) {

  const fields = []

  for (const component of components || []) {

    if (!component || typeof component !== 'object') continue

    if (isCardLayoutNode(component)) {

      fields.push(...flattenCardFields(component.components))
      continue
    }

    fields.push(component)
  }

  return fields
}

// A card column can take the same width as a page column: a share of the
// card, or a fixed pixel width. An empty width shares the card. Overflow
// belongs to page columns, which hold views, so it is not kept here.
function cardColumnWidth (column) {

  if (!column || typeof column !== 'object') return null

  const record = columnFormatRecord(column.columnFormat)

  if (!record || record.width == null || !record.unit) return null

  return { width: record.width, unit: record.unit }
}

function assignCardColumnWidth (column, value) {

  if (!column || typeof column !== 'object') return

  const width = cardColumnWidth({ columnFormat: value })

  if (width) column.columnFormat = width
  else delete column.columnFormat
}

function cardColumnNode (column, components) {

  const stored = { type: CARD_COLUMN, components }
  const width = cardColumnWidth(column)

  if (width) stored.columnFormat = width

  return stored
}

function columnsOfRow (row) {

  const children = Array.isArray(row && row.components) ? row.components : []
  const columns = []
  const loose = []

  for (const child of children) {

    if (!child || typeof child !== 'object') continue

    if (child.type === CARD_COLUMN) {

      columns.push(cardColumnNode(child, flattenCardFields(child.components)))
      continue
    }

    if (child.type === CARD_ROW) {

      columns.push(...columnsOfRow(child))
      continue
    }

    loose.push(child)
  }

  if (loose.length) columns.unshift({ type: CARD_COLUMN, components: loose })
  if (!columns.length) columns.push({ type: CARD_COLUMN, components: [] })

  return columns
}

// Rows of columns of the original field objects. A flat field list is one
// row with one column, which is how older cards were stored.
function cardLayoutRows (components) {

  const list = (Array.isArray(components) ? components : []).filter(item => item && typeof item === 'object')
  const rows = list.filter(item => item.type === CARD_ROW)

  if (!rows.length) {

    return [{
      type: CARD_ROW,
      components: [{ type: CARD_COLUMN, components: flattenCardFields(list) }]
    }]
  }

  const laid = rows.map(row => ({
    type: CARD_ROW,
    components: columnsOfRow(row)
  }))
  const loose = flattenCardFields(list.filter(item => item.type !== CARD_ROW))

  if (loose.length) laid[0].components.unshift({ type: CARD_COLUMN, components: loose })

  return laid
}

// Editable tree for the designer. Existing card rows are kept. A flat list
// becomes one row and one column. An empty list gets one empty column so a
// field has a place to land.
function prepareCardLayout (components) {

  const list = (Array.isArray(components) ? components : []).filter(item => item && typeof item === 'object')
  const rows = list.filter(item => item.type === CARD_ROW)

  const loose = flattenCardFields(list.filter(item => item.type !== CARD_ROW))

  if (!rows.length) {

    return [{
      type: CARD_ROW,
      components: [{ type: CARD_COLUMN, components: loose }]
    }]
  }

  const prepared = rows.map(row => {

    const children = Array.isArray(row.components) ? row.components : []
    const columns = children.filter(item => item && item.type === CARD_COLUMN).map(column => {

      column.components = flattenCardFields(column.components)
      return column
    })
    const beside = flattenCardFields(children.filter(item => item && item.type !== CARD_COLUMN))

    if (beside.length) columns.unshift({ type: CARD_COLUMN, components: beside })
    if (!columns.length) columns.push({ type: CARD_COLUMN, components: [] })

    row.components = columns
    return row
  })

  if (loose.length) {

    const first = prepared[0].components[0]
    first.components = loose.concat(first.components || [])
  }

  return prepared
}

function cardLayoutFromFields (fields) {

  return [{
    type: CARD_ROW,
    components: [{
      type: CARD_COLUMN,
      components: Array.isArray(fields) ? fields : []
    }]
  }]
}

function isOneCardPerRow (value) {

  return value === true || value === 'true'
}

// A whole number from 1 to MAX_CARDS_PER_ROW, or null when the value is
// blank or not a usable count. Out-of-range numbers are null so a caller
// can warn instead of storing them.
function normalizeCardsPerRow (value) {

  if (typeof value === 'boolean' || value == null || value === '') return null

  const number = typeof value === 'number' ? value : Number(String(value).trim())

  if (!Number.isFinite(number)) return null
  if (Math.abs(number - Math.round(number)) > 1e-9) return null

  const rounded = Math.round(number)

  if (rounded < 1 || rounded > MAX_CARDS_PER_ROW) return null

  return rounded
}

// The designer number box. Blank stays automatic. A usable number is
// clamped into range so typing past the maximum still saves a layout.
function cardsPerRowFromInput (value) {

  if (value == null) return null

  const text = String(value).trim()

  if (!text) return null

  const number = Number(text)

  if (!Number.isFinite(number)) return null

  const rounded = Math.round(number)

  if (rounded < 1) return null
  if (rounded > MAX_CARDS_PER_ROW) return MAX_CARDS_PER_ROW

  return rounded
}

// How many cards share a row. Null means the list wraps as the column
// allows. A single-record card always uses the full row.
function cardsAcross (component) {

  if (!component || typeof component !== 'object') return null
  if (component.detail === true || component.type === 'card') return 1
  if (!isCardView(component)) return null

  const count = normalizeCardsPerRow(component.cardsPerRow)

  if (count) return count
  if (isOneCardPerRow(component.oneCardPerRow)) return 1

  return null
}

function cardUsesOnePerRow (component) {

  if (!isCardView(component)) return false

  const count = normalizeCardsPerRow(component.cardsPerRow)

  if (count === 1) return true
  if (count != null) return false

  return isOneCardPerRow(component.oneCardPerRow)
}

// Equal tracks that fill the row. Empty when the list should wrap itself.
function cardGridColumns (count) {

  const cards = normalizeCardsPerRow(count)

  if (!cards) return ''

  return `repeat(${cards}, minmax(0, 1fr))`
}

// Writes the count onto a cards list and keeps the older flag in step.
// A count of 1 is the full-width list. Clearing returns the wrapping layout.
function assignCardsPerRow (view, count) {

  if (!view || typeof view !== 'object') return

  if (count == null) {

    delete view.cardsPerRow
    view.oneCardPerRow = false
    return
  }

  view.cardsPerRow = count
  view.oneCardPerRow = count === 1
}

module.exports = {
  CARD_VIEW_TYPES,
  CARD_ROW,
  CARD_COLUMN,
  MAX_CARDS_PER_ROW,
  isCardView,
  isDetailCard,
  isCardLayoutNode,
  isOneCardPerRow,
  normalizeCardsPerRow,
  cardsPerRowFromInput,
  cardsAcross,
  cardUsesOnePerRow,
  cardGridColumns,
  assignCardsPerRow,
  flattenCardFields,
  cardLayoutRows,
  prepareCardLayout,
  cardLayoutFromFields,
  cardColumnWidth,
  assignCardColumnWidth
}
